import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { ErrorResponse, ListResponse } from '../models';

export type ApiResponse<T> = T | ErrorResponse;

export function isErrorResponse(response: any): response is ErrorResponse {
  return response && typeof response === 'object' && 'error' in response;
}

export function isSuccessResponse<T>(response: any): response is T {
  return !isErrorResponse(response);
}

export abstract class BaseService<T> {
  protected baseUrl: string;

  constructor(protected http: HttpClient, baseUrl: string) {
    this.baseUrl = environment.apiBaseUrl + baseUrl;
  }

  // Create new item
  create(data: Partial<T>): Observable<T | ErrorResponse> {
    return this.http.post<ApiResponse<T>>(`${this.baseUrl}/new`, data).pipe(
      map(response => this.handleResponse<T>(response)));
  }

  // List all items
  list(params?: any): Observable<ListResponse<T>> {
    return this.http.get<ListResponse<T>>(`${this.baseUrl}/list`, { params });
  }

  // Get single item by ID
  get(id: string): Observable<T | ErrorResponse> {
    return this.http.get<ApiResponse<T>>(`${this.baseUrl}/${id}`).pipe(
      map(response => this.handleResponse<T>(response)));
  }

  // Update existing item
  update(id: string, data: Partial<T>): Observable<T | ErrorResponse> {
    return this.http.put<ApiResponse<T>>(`${this.baseUrl}/${id}`, data);
  }

  // Delete item
  delete(id: string): Observable<T | ErrorResponse> {
    return this.http.delete<ApiResponse<T>>(`${this.baseUrl}/${id}`);
  }

  private handleResponse<T>(response: ApiResponse<T>): T {
    if (isErrorResponse(response)) {
      throw new Error(response.error);
    }
    return response as T;
  }
}
